import React from "react";
import { GiElephant, GiCutDiamond } from "react-icons/gi";

/* =============================================================
   Kutch vintage decorative motifs (all static — no rotation).
   Colour follows `currentColor`, so tint with Tailwind text-*.
   ============================================================= */

// Tiled block-print (Kutch buti) background — subtle heritage texture layer.
export function BlockPrintBg({ className = "", opacity = 0.1 }) {
  const c = "%23c9a24b";
  const tile =
    `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E` +
    `%3Cg fill='none' stroke='${c}' stroke-width='1.2'%3E` +
    // central paisley / buti
    `%3Cpath d='M40 22 C50 24 54 34 48 42 C44 47 36 47 33 42 C30 37 33 31 39 31 C43 31 45 35 42 37'/%3E` +
    // four corner diamonds
    `%3Cpath d='M40 4 L46 10 L40 16 L34 10 Z'/%3E` +
    `%3Cpath d='M4 40 L10 46 L16 40 L10 34 Z'/%3E` +
    `%3Cpath d='M76 40 L70 46 L64 40 L70 34 Z'/%3E` +
    `%3Cpath d='M40 76 L46 70 L40 64 L34 70 Z'/%3E` +
    `%3C/g%3E%3C/svg%3E`;
  return (
    <div
      className={className}
      aria-hidden="true"
      style={{
        opacity,
        backgroundImage: `url("${tile}")`,
        backgroundRepeat: "repeat",
        backgroundSize: "80px 80px",
      }}
    />
  );
}

// Repeating Ajrakh / block-print band (horizontal divider strip).
// Rendered as a repeating background tile so the diamond motif keeps its
// aspect ratio at any width (no stretching).
export function BlockBand({ className = "" }) {
  const c = "%23c9a24b";
  const tile =
    `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='44' height='22' viewBox='0 0 44 22'%3E` +
    `%3Cg fill='none' stroke='${c}' stroke-opacity='0.6' stroke-width='1.3'%3E` +
    `%3Cpath d='M22 2 L42 11 L22 20 L2 11 Z'/%3E` +
    `%3Cpath d='M0 11 H44' stroke-width='0.6'/%3E%3C/g%3E` +
    `%3Ccircle cx='22' cy='11' r='2.2' fill='${c}' fill-opacity='0.6'/%3E%3C/svg%3E`;
  return (
    <div
      className={className}
      aria-hidden="true"
      style={{
        backgroundImage: `url("${tile}")`,
        backgroundRepeat: "repeat-x",
        backgroundPosition: "center",
        backgroundSize: "44px 22px",
      }}
    />
  );
}

// Scalloped arch divider (a row of mehrab arches) — repeating tile, fixed aspect.
export function ScallopDivider({ className = "" }) {
  const c = "%23c9a24b";
  const tile =
    `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='12' viewBox='0 0 32 12'%3E` +
    `%3Cpath d='M0 12 Q16 -3 32 12' fill='none' stroke='${c}' stroke-opacity='0.75' stroke-width='1.4'/%3E%3C/svg%3E`;
  return (
    <div
      className={className}
      aria-hidden="true"
      style={{
        backgroundImage: `url("${tile}")`,
        backgroundRepeat: "repeat-x",
        backgroundPosition: "center",
        backgroundSize: "32px 12px",
      }}
    />
  );
}

// Mandala / rangoli corner ornament
export function MandalaCorner({ className = "" }) {
  const petals = Array.from({ length: 6 });
  return (
    <svg className={className} viewBox="0 0 120 120" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="1.4">
        <path d="M2 2 H40 M2 2 V40" strokeWidth="2" />
        {petals.map((_, i) => {
          const a = (i * 15 * Math.PI) / 180;
          const x = 8 + Math.cos(a) * 70;
          const y = 8 + Math.sin(a) * 70;
          return <path key={i} d={`M8 8 Q${x * 0.5} ${y * 0.5} ${x} ${y}`} opacity={0.5} />;
        })}
        {[18, 34, 50].map((r) => (
          <path key={r} d={`M8 ${8 + r} A${r} ${r} 0 0 1 ${8 + r} 8`} />
        ))}
        <circle cx="8" cy="8" r="4" fill="currentColor" />
      </g>
    </svg>
  );
}

// Bandhani (tie-dye) dotted field — use as a subtle background overlay
export function BandhaniField({ className = "", opacity = 0.12 }) {
  return (
    <div
      className={className}
      aria-hidden="true"
      style={{
        opacity,
        backgroundImage:
          "radial-gradient(currentColor 1.4px, transparent 1.6px), radial-gradient(currentColor 1.4px, transparent 1.6px)",
        backgroundSize: "26px 26px",
        backgroundPosition: "0 0, 13px 13px",
      }}
    />
  );
}

// Ornate peacock (tl.png silhouette) recoloured to the theme via a CSS mask.
// The PNG's alpha shape is filled with `currentColor`, so tint with text-*.
export function Peacock({ className = "" }) {
  const mask = "url(/assets/tl.png) center / contain no-repeat";
  return (
    <div
      className={className}
      aria-hidden="true"
      style={{
        backgroundColor: "currentColor",
        WebkitMask: mask,
        mask,
      }}
    />
  );
}

// Kutchi elephant motif — uses the well-drawn Game-Icons silhouette.
export function Elephant({ className = "" }) {
  return <GiElephant className={className} aria-hidden="true" />;
}

// Pointed arch / keystone ornament (placed at apex of arch frames)
export function Keystone({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <path d="M20 2 L26 14 L20 26 L14 14 Z" fill="currentColor" opacity="0.9" />
      <circle cx="20" cy="14" r="3" fill="#2b1a0f" />
      <path d="M20 26 L20 38" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

// Small ornamental separator with a cut-diamond at centre.
export function Ornament({ className = "" }) {
  return (
    <span className={`inline-flex items-center gap-3 ${className}`} aria-hidden="true">
      <span className="h-px w-12 bg-gradient-to-r from-transparent to-gold" />
      <GiCutDiamond className="text-gold" size={16} />
      <span className="h-px w-12 bg-gradient-to-l from-transparent to-gold" />
    </span>
  );
}

// Arched (jharokha) image frame — soft mehrab arch with gold double border
export function ArchImage({ src, alt, className = "", imgClass = "" }) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-10 text-gold">
        <Keystone className="w-7 h-7" />
      </div>
      <div className="rounded-t-[48%] rounded-b-md overflow-hidden ring-2 ring-gold/70 shadow-vintage bg-coffee-900 p-1.5">
        <div className="rounded-t-[46%] rounded-b-md overflow-hidden ring-1 ring-gold/40 h-full">
          <img src={src} alt={alt} className={`w-full h-full object-cover ${imgClass}`} loading="lazy" />
        </div>
      </div>
    </div>
  );
}
